import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import Promise from 'bluebird';
import _ from 'lodash';

// Services
import Ticket from 'services/data/ticket';
import Email from 'services/data/email';
import TimeReport from 'services/data/time-report';
import PurchaseOrder from 'services/data/purchase-order';
import File from 'services/data/file';
import Triage from 'services/data/triage';
import { useSocket } from 'services/socket';

// Utils
import { prepEagerUpdate } from 'utils/eager-update-utils';
import { mergeSocketObject } from 'utils/socket-utils';
import replaceById from 'utils/replace-by-id';
import isEqualString from 'utils/is-equal-string';

// Loads mirror the legacy flux loaders in ../utils/load-ticket.js so the data
// shape is identical to what the redux-connected component received.
const TICKET_LOAD = {
  agreement: null,
  mergedTo: null,
  'agreement.rates': null,
  folder: null,
  closedBy: null,
  tasks: { status: 'Active' },
  'tasks.assignedTo': null,
  'tasks.assignedTo.team': null,
  'tasks.checklist': null,
  'createdBy.team': null,
  company: null,
  'company.team': null,
  workOrder: null
};

const TRIAGE_LOAD = [
  'fromPerson', 'company', 'claimedBy.team', 'ticket', 'userOnCall.team',
  'lastUserOnCall', 'transferringTo', 'recording', 'replyUser'
];

const EMAILS_LOAD = ['fromPerson.team', 'attachments'];

const TIME_REPORTS_LOAD = ['agreement.rates', 'rate', 'ticket.agreement'];

const PURCHASE_ORDERS_LOAD = [
  'ticket.agreement', 'agreement', 'company', 'createdBy', 'lineItems',
  'lineItems.expenseBillLineItems', 'expenseBills.lineItems.purchaseOrderLineItem',
  'expenseBills.receipt', 'expenseBills.agreement', 'expenseBills.ticket.agreement'
];

export const ticketKeys = {
  detail: (id) => ['ticket', id],
  closedTasks: (id, closedTaskLimit) => ['ticket', id, 'closed-tasks', closedTaskLimit],
  triage: (id) => ['ticket', id, 'triage'],
  files: (id) => ['ticket', id, 'files'],
  timeReports: (id) => ['ticket', id, 'timeReports'],
  emails: (id) => ['ticket', id, 'emails'],
  purchaseOrders: (id) => ['ticket', id, 'purchaseOrders']
};

// Load once and keep fresh via the socket + explicit invalidation rather than
// refetch-on-mount. This is also what keeps the checklist merge safe: the only
// writes to the detail cache are the controlled merge and eager mutations, so a
// second observer (a sub-route mounting alongside the page) can never trigger an
// unmerged refetch that clobbers local edits.
const QUERY_OPTIONS = { staleTime: Infinity };

/**
 * Loads a single ticket and all of its related data (triage, files, time
 * reports, emails, purchase orders) via react-query, and exposes cache-update
 * callbacks (the modern equivalent of the old actions.updateSingleTicket /
 * actions.set / util.load*) for the page and its sub-routes.
 *
 * react-query dedupes by key, so the page and the active sub-route can both call
 * this and share a single set of fetches/caches. Pass `{ socket: true }` from
 * exactly one of them (the page) to keep the ticket live over the socket.
 *
 * @param {string} ticketId
 * @param {{ socket?: boolean }} [options] - socket defaults to false.
 */
export default function useTicket(ticketId, { socket = false, closedTaskLimit = 10 } = {}) {
  const queryClient = useQueryClient();
  const enabled = !!ticketId;

  const ticketQuery = useQuery(
    ticketKeys.detail(ticketId),
    () => Ticket.findOne(ticketId, { load: TICKET_LOAD }),
    { enabled, ...QUERY_OPTIONS }
  );
  const ticket = ticketQuery.data;

  const closedTaskQuery = useQuery(
    ticketKeys.closedTasks(ticketId, closedTaskLimit),
    () => Ticket.findOne(ticketId, { load: { tasks: { status: { in: ['Complete'] }, closedLimit: closedTaskLimit }}}),
    { enabled, ...QUERY_OPTIONS }
  );

  const triageQuery = useQuery(
    ticketKeys.triage(ticketId),
    () => Triage.find({ load: TRIAGE_LOAD, limit: 10000, where: { ticket: ticketId } }),
    { enabled, ...QUERY_OPTIONS }
  );

  const timeReportsQuery = useQuery(
    ticketKeys.timeReports(ticketId),
    () => TimeReport.find({ load: TIME_REPORTS_LOAD, where: { ticket: ticketId } }),
    { enabled, ...QUERY_OPTIONS }
  );

  const emailsQuery = useQuery(
    ticketKeys.emails(ticketId),
    () => Email.find({ load: EMAILS_LOAD, where: { ticket: ticketId } }),
    { enabled, ...QUERY_OPTIONS }
  );

  const purchaseOrdersQuery = useQuery(
    ticketKeys.purchaseOrders(ticketId),
    () => PurchaseOrder.find({ load: PURCHASE_ORDERS_LOAD, where: { ticket: ticketId } }),
    { enabled, ...QUERY_OPTIONS }
  );

  const folderId = ticket && ticket.folder && ticket.folder.id;
  const filesQuery = useQuery(
    ticketKeys.files(ticketId),
    () => File.list(folderId),
    { enabled: enabled && !!folderId, ...QUERY_OPTIONS }
  );

  // --- Cache-update callbacks (replace actions.updateSingleTicket / set) ---

  const updateTicket = useCallback((updates) => {
    queryClient.setQueryData(ticketKeys.detail(ticketId), (old) => prepEagerUpdate(old, updates));
  }, [queryClient, ticketId]);

  const updateTask = useCallback((task, ticketUpdates) => {
    queryClient.setQueryData(ticketKeys.detail(ticketId), (old) => {
      const tasks = replaceById(prepEagerUpdate(task), old.tasks);
      return prepEagerUpdate(old, { tasks, ...ticketUpdates });
    });
  }, [queryClient, ticketId]);

  const updateTime = useCallback((timeReport) => {
    queryClient.setQueryData(ticketKeys.timeReports(ticketId), (reports) => replaceById(timeReport, reports));
  }, [queryClient, ticketId]);

  const updateTriage = useCallback((triageItems) => {
    queryClient.setQueryData(ticketKeys.triage(ticketId), triageItems);
  }, [queryClient, ticketId]);

  const updatePurchaseOrders = useCallback((update) => {
    queryClient.setQueryData(ticketKeys.purchaseOrders(ticketId), (orders) => replaceById(update, orders, { skipMerge: true }));
  }, [queryClient, ticketId]);

  const saveNewTask = useCallback((description, properties) =>
    Ticket.saveNewTask(ticket, { description, ...properties }, updateTask)
  , [ticket, updateTask]);

  // Refetch helpers for child routes that previously called util.load* directly
  // (e.g. after submitting a time report, creating a PO, or adding a file).
  const reloadFiles = useCallback(() => queryClient.invalidateQueries(ticketKeys.files(ticketId)), [queryClient, ticketId]);
  const reloadTimeReports = useCallback(() => queryClient.invalidateQueries(ticketKeys.timeReports(ticketId)), [queryClient, ticketId]);
  const reloadPurchaseOrders = useCallback(() => queryClient.invalidateQueries(ticketKeys.purchaseOrders(ticketId)), [queryClient, ticketId]);

  // --- Live updates over the ticket socket (opt-in via options.socket) ---
  // People make fast, frequent checklist edits, so a plain refetch can clobber
  // in-flight local state. We reload the ticket and merge it against the cache
  // (preserving checklist edits via locked/updatedAt), exactly like the legacy
  // mergeSocketTicket. Debounced to match the old debouncedLoadTicket. Only one
  // caller (the page) should enable this so the merge runs once per event.
  const refresh = useMemo(() => _.debounce(() => {
    Promise.resolve(Ticket.findOne(ticketId, { load: TICKET_LOAD }))
      .then((loaded) => {
        const current = queryClient.getQueryData(ticketKeys.detail(ticketId));
        const merged = mergeSocketObject(loaded, current, ['tasks.checklist'], { keepItemsFilters: [{ status: 'Active' }] });
        queryClient.setQueryData(ticketKeys.detail(ticketId), merged);
      })
      .catch(_.noop);

    queryClient.invalidateQueries(ticketKeys.timeReports(ticketId));
    queryClient.invalidateQueries(ticketKeys.purchaseOrders(ticketId));
  }, 20 * 1000, { maxWait: 60 * 1000 }), [ticketId, queryClient]);

  // Cancel any pending refresh when the ticket changes or the component unmounts.
  useEffect(() => () => refresh.cancel(), [refresh]);

  const handleSocketUpdate = useCallback((payload) => {
    // onReconnect fires with no payload; otherwise ignore other tickets.
    if (payload && !isEqualString(ticketId, payload.id)) return;
    refresh();
  }, [ticketId, refresh]);

  useSocket('ticket', handleSocketUpdate, handleSocketUpdate, socket);

  return {
    ticket,
    isFetching: ticketQuery.isLoading,
    closedTasks: closedTaskQuery.data?.tasks || [],
    closedTaskCount: closedTaskQuery.data?.closedTaskCount || 0,
    isFetchingClosedTasks: closedTaskQuery.isLoading,
    triageItems: triageQuery.data,
    files: filesQuery.data,
    isFetchingFiles: filesQuery.isFetching,
    timeReports: timeReportsQuery.data,
    emails: emailsQuery.data,
    purchaseOrders: purchaseOrdersQuery.data,
    // mutators
    updateTicket,
    updateTask,
    updateTime,
    updateTriage,
    updatePurchaseOrders,
    saveNewTask,
    // refetch helpers
    reloadFiles,
    reloadTimeReports,
    reloadPurchaseOrders
  };
}
